import { useState } from 'react';

/**
 * Keeps a first read's failure on screen through its *Retry*. TanStack Query v5 puts a key that has no
 * data back to `pending` on a refetch and clears its error, so the error panel, and the focused *Retry*
 * inside it, would leave the page at the press: a failed retry would then come back as a new *Retry*
 * with focus on `<body>`. `hold()` keeps the failure from the press until the read publishes its next
 * result — data, or a new error (its `errorUpdateCount` moves) — so a failed retry leaves the same
 * *Retry* in place and focused, and a successful one shows the list. The hold is matched on the error's
 * `errorUpdatedAt` as well as the count, so it never carries over to another key (a new `version` or
 * search) whose cached count happens to be equal: that key's first read shows its own loading state. `useAccountDetail`'s kept first
 * error (`src/features/accounts/accountsQueries.ts`) is the same rule, and like it the value is kept
 * with React's "adjust state while rendering" pattern rather than a ref written during render.
 */
export interface RetryHold {
    /** The failure kept on screen while its *Retry* re-reads, or `null`. */
    readonly held: { readonly error: unknown } | null;
    /** Call from the *Retry* press, before the refetch. */
    readonly hold: () => void;
}

export function useRetryHold(read: {
    readonly error: unknown;
    readonly isError: boolean;
    readonly hasData: boolean;
    readonly errorUpdateCount: number;
    readonly errorUpdatedAt: number;
}): RetryHold {
    const { error, isError, hasData, errorUpdateCount, errorUpdatedAt } = read;
    const [kept, setKept] = useState<{
        readonly error: unknown;
        readonly errorUpdates: number;
        readonly errorAt: number;
    } | null>(null);
    const current =
        kept !== null && !hasData && kept.errorUpdates === errorUpdateCount && kept.errorAt === errorUpdatedAt
            ? kept
            : null;
    if (current !== kept) {
        setKept(current);
    }
    return {
        held: current === null ? null : { error: current.error },
        hold: () => {
            if (isError && !hasData) {
                setKept({ error, errorUpdates: errorUpdateCount, errorAt: errorUpdatedAt });
            }
        },
    };
}
